import { CopyOutlined } from "@ant-design/icons";
import { Modal, notification } from "antd";
import { Form, InputNumber } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import {
    apiCall,
    getOrderClosedCheckoutListThunk,
    getOrderOpenedCheckoutListThunk,
    postMessengerReceivedDelivery,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { PromoGood } from "tm-shared/types";
import { v7 } from "uuid";

import BaseButton from "@/components/BaseButton";
import FlexContainer from "@/components/FlexContainer";
import { MarketCard } from "@/components/MarketCard";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { StyledButton } from "@/pages/HR/AddUser/Styled";
import { clearMainCheckout } from "@/store/slices/work/work.slice";
import { ModalPayloads, ModalType } from "@/types/modals";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { WarningModal } from "../Warning";
import { StyledPromoTable } from "./Styled";

export interface GiftItem {
    id: string;
    name: React.ReactNode;
    quantityAvailable: number;
}

function getTimesWord(n: number): string {
    const abs = Math.abs(n);
    const lastDigit = abs % 10;
    const lastTwoDigits = abs % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return "разів";
    }

    if (lastDigit === 1) {
        return "раз";
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return "рази";
    }

    return "разів";
}

export interface PromoModalProps {
    payload: ModalPayloads[ModalType.Promos];
    onCloseModal: () => void;
}

const PromoModal: React.FC<PromoModalProps> = ({ onCloseModal, payload }) => {
    const dispatch = useAppDispatch();
    const { id: userId, role, phone } = useAppSelector((state) => state.user);
    const { markets } = useAppSelector((state) => state.work);
    const { activeSchedule } = useAppSelector((state) => state.sv);

    const isManager = isManagerRole(role);

    const trueAgentId = isManager ? activeSchedule : userId;

    const { promoInfo, deliveryId } = payload;

    const [form] = Form.useForm();
    const [clickedButton, setClickedButton] = useState(false);
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [isLoadingConfirm, setIsLoadingConfirm] = useState(false);

    const rejectModalKey = useMemo(() => v7(), [deliveryId]);

    useEffect(() => {
        if (deliveryId) {
            postMessengerReceivedDelivery({
                deliveryId,
            });
        }
    }, [deliveryId]);

    if (!promoInfo) return null;

    const handleWarningModalClose = () => {
        setIsWarningModalOpen(false);
    };

    const columns: ColumnsType<GiftItem> = [
        {
            title: "Подарунок",
            dataIndex: "name",
            key: "name",
            width: "80%",
        },
        {
            title: "Обрана кількість",
            dataIndex: "selectedQuantity",
            key: "selectedQuantity",
            width: "20%",
            render: (_, record) => {
                const fieldName = ["gifts", record.id];

                return (
                    <Form.Item
                        name={fieldName}
                        initialValue={0}
                        style={{ margin: 0 }}
                        rules={[
                            {
                                validator: (_, value) => {
                                    const gifts =
                                        form.getFieldValue("gifts") || {};
                                    const totalWithoutCurrent = Object.entries(
                                        gifts,
                                    ).reduce((acc, [key, val]) => {
                                        if (key === record.id) return acc;
                                        return acc + ((val as number) || 0);
                                    }, 0);
                                    const totalWithCurrent =
                                        totalWithoutCurrent + (value || 0);

                                    if (
                                        promoInfo?.promoScale !== undefined &&
                                        totalWithCurrent > promoInfo.promoScale
                                    ) {
                                        return Promise.reject(new Error());
                                    }

                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <InputNumber
                            min={0}
                            onFocus={(event) => event?.target?.select()}
                            max={record.quantityAvailable}
                            disabled={record.quantityAvailable === 0}
                        />
                    </Form.Item>
                );
            },
        },
    ];

    const data = Object.values(promoInfo?.promoGoods).map(
        (item: PromoGood) => ({
            id: item.uniquePresentCode,
            name: (
                <FlexContainer
                    flexDirection="column"
                    gap="4px"
                    style={{ width: "100%" }}
                    alignItems="flex-start"
                >
                    <PrimaryText>{item.name}</PrimaryText>
                    <SecondaryText>
                        Доступна кількість: {item.stockBalance}
                    </SecondaryText>
                </FlexContainer>
            ),
            quantityAvailable: item.stockBalance,
        }),
    );

    const handleRejectPresent = async () => {
        setIsWarningModalOpen(true);
    };

    const handleWarningApproved = async () => {
        setIsLoadingConfirm(true);
        try {
            await apiCall({
                method: promoInfo?.rejectCallback?.method,
                params: {
                    ...promoInfo?.rejectCallback?.params,
                    deliveryId: deliveryId,
                },
            });

            setIsWarningModalOpen(false);
            form.resetFields();
        } catch (err) {
            console.error("API error:", err);
        } finally {
            setIsLoadingConfirm(false);
            handleClosePromoModal();
        }
    };

    const handleClosePromoModal = () => {
        setClickedButton(false);
        onCloseModal();
        form.resetFields();
    };

    const handleCopyPromoInfo = () => {
        copyObjectToClipboard({
            id: promoInfo?.id,
            userId: trueAgentId,
            contactId: phone,
            marketId: promoInfo?.marketId,
            promoGoods: promoInfo?.promoGoods,
            orderId: promoInfo?.orderId,
            promoId: promoInfo?.promoId,
            consumedId: promoInfo?.consumedId,
            orientedCount: promoInfo?.orientedCount,
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const gifts = values?.gifts || {};
            const totalSum = Object.entries(gifts).reduce((acc, el) => {
                return acc + ((el[1] as number) || 0);
            }, 0);

            if (totalSum === 0) {
                notification.error({
                    message: "Оберіть хоча б один подарунок",
                });
                return;
            }

            setClickedButton(true);

            await apiCall({
                method: promoInfo?.callback?.method,
                params: {
                    ...promoInfo?.callback?.params,
                    selectPresents: Object.fromEntries(
                        Object.entries(gifts).filter(
                            (present) => (present[1] as number) > 0,
                        ),
                    ),
                    deliveryId: deliveryId,
                },
            });

            setTimeout(async () => {
                setClickedButton(false);
                handleClosePromoModal();
                dispatch(clearMainCheckout());
                await dispatch(
                    getOrderOpenedCheckoutListThunk({ agentId: trueAgentId }),
                );
                await dispatch(
                    getOrderClosedCheckoutListThunk({
                        agentId: trueAgentId,
                        targetDate: dayjs().format(backendDateFormat.date),
                    }),
                );
                notification.success({
                    message: "Інвойс успішно створений",
                });
                form.resetFields();
            }, 10000);
        } catch (error) {
            console.error("Validation failed:", error);

            const code =
                error?.response?.data?.error?.code ??
                error?.code ??
                error?.message;

            if (String(code) === "-32015") {
                notification.error({
                    message: "Замовлення по цій акції вже створено",
                });
                handleClosePromoModal();
            } else {
                notification.error({
                    message: "Помилка при оформленні інвойсу",
                });
            }

            setClickedButton(false);
        }
    };

    const checkMaxProducts = () => {
        const gifts =
            (form.getFieldValue("gifts") as Record<string, number>) || {};
        const totalSum = Object.values(gifts).reduce(
            (acc, val) => acc + ((val as number) || 0),
            0,
        );
        if (
            promoInfo?.promoScale !== undefined &&
            totalSum > promoInfo.promoScale
        ) {
            notification.error({
                message: `Максимальна кількість подарункових пляшок — ${promoInfo.promoScale} шт.`,
            });
        }
    };

    const handleValuesChange = () => {
        form.validateFields();
    };

    const marketAddress = markets?.[promoInfo?.marketId]?.address?.name || "-";
    const marketFop = markets?.[promoInfo?.marketId]?.contragent?.name || "-";
    const marketName = markets?.[promoInfo?.marketId]?.name || "-";

    return (
        <>
            <Modal open title="" closable={false} centered footer={[]}>
                <FlexContainer>
                    <PrimaryText
                        fontSize={16}
                        fontWeight="500"
                        textAlign="center"
                        margin="0 0 16px 0"
                        padding="0 0 3px 0"
                        style={{
                            borderBottom: "1px dashed #5f5f5f",
                        }}
                        onClick={handleCopyPromoInfo}
                    >
                        {promoInfo?.title} <CopyOutlined />
                    </PrimaryText>
                </FlexContainer>

                <MarketCard fop={marketFop} address={marketAddress} />
                <SecondaryText
                    fontSize={14}
                    lineHeight="140%"
                    fontWeight="400"
                    padding="16px 0"
                    textAlign="left"
                >
                    {promoInfo?.description}
                </SecondaryText>

                <PrimaryText style={{ textAlign: "left", padding: "16px 0" }}>
                    Умови акції виконані {promoInfo?.promoScale}{" "}
                    {getTimesWord(promoInfo?.promoScale)}. Оберіть кількість
                    подарункових пляшок:
                </PrimaryText>
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    onValuesChange={handleValuesChange}
                >
                    <StyledPromoTable
                        rowKey="id"
                        size="small"
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        rowClassName={(record) =>
                            record.quantityAvailable === 0 ? "disabled-row" : ""
                        }
                    />

                    <StyledButton
                        type="primary"
                        htmlType="submit"
                        disabled={clickedButton}
                        loading={clickedButton}
                        onClick={checkMaxProducts}
                        style={{
                            backgroundColor: clickedButton ? "gray" : "#49AA19",
                            marginTop: "16px",
                            color: "#fff",
                        }}
                    >
                        Оформити подарунок
                    </StyledButton>
                </Form>
                <BaseButton
                    onClick={handleRejectPresent}
                    disabled={clickedButton}
                    marginTop="16px"
                    type="default"
                >
                    Не оформляти
                </BaseButton>
            </Modal>
            <WarningModal
                modalKey={rejectModalKey}
                width="92%"
                isModalOpened={isWarningModalOpen}
                confirm={handleWarningApproved}
                cancel={handleWarningModalClose}
                children={
                    <PrimaryText margin="20px 0 0 0">
                        Ви впевнені, що хочете відмовитися від оформлення
                        подарункових пляшок для{" "}
                        <strong>
                            {marketName}, {marketAddress}
                        </strong>{" "}
                        ? Після підтвердження цієї дії повернутися до оформлення
                        буде неможливо!
                    </PrimaryText>
                }
                maskClosable={false}
                isLoadingConfirm={isLoadingConfirm}
            />
        </>
    );
};

export default PromoModal;
